/**
 * The Training save queue (ISS-053, ISS-054, ISS-055, ISS-060).
 *
 * Progress events go to the server one batch at a time, in the order they happened,
 * so a later change can never land before an earlier one; everything queued while a
 * batch is out goes in the next one. A batch that fails for a passing reason
 * (offline, a server error) stays at the front of the queue under the same event ids
 * (the server skips a repeat) and is sent again after a short wait. A finished exam
 * whose result didn't reach the server is kept and sent the same way. Both are kept
 * in this browser for the signed-in person, so closing the page first doesn't lose
 * them: the next visit sends them before it reads progress. A request the server
 * refuses for good (a 4xx) is dropped, because sending it again can't succeed.
 *
 * Each event keeps the company its progress was read for and is sent with it, so
 * progress saved under one company is never written into another (the server answers
 * 409 company_changed, and it is dropped). #1200 review.
 */
import type { FinishedExam, TrainingEvent } from './trainingApi';

export const RETRY_FIRST_MS = 5_000;
const RETRY_MAX_MS = 5 * 60_000;
/** Events per request: a finished 50-question exam goes in one. */
const MAX_BATCH = 100;
/** Kept at most while the server can't be reached, newest kept. */
const MAX_KEPT_EVENTS = 2_000;

export interface FinishAnswer { question_id: string; choice: number | null }
interface PendingFinish { attemptId: string; answers: FinishAnswer[] }
interface QueuedEvent { companyId: string | null; event: TrainingEvent }
interface Saved { v: 2; events: QueuedEvent[]; finishes: PendingFinish[] }

export interface OutboxDeps {
  postEvents: (events: TrainingEvent[], companyId: string | null) => Promise<unknown>;
  finishExam: (attemptId: string, answers: FinishAnswer[]) => Promise<FinishedExam>;
  /** Where unsent work is kept between visits, or null to keep it for this visit only. */
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null;
  storageKey: string | null;
  /** A finish the server accepted (not one it already had). */
  onFinished?: (exam: FinishedExam) => void;
}

/** The browser key for one person's unsent Training work. */
export const outboxKey = (userId: string) => `ht:training-outbox:${userId}`;

/**
 * Worth sending again: no answer (offline, timeout), a server error, "slow down", or a
 * sign-in that had expired (401). The session refreshes, so a later try goes through.
 * A sign-out stops the queue, so a 401 is never retried for someone else.
 */
export function isRetryable(err: unknown): boolean {
  const status = (err as { status?: unknown } | null)?.status;
  if (typeof status !== 'number') return true;
  return status >= 500 || status === 401 || status === 408 || status === 429;
}

const isEvent = (e: unknown): e is TrainingEvent =>
  !!e && typeof (e as { event_id?: unknown }).event_id === 'string' && typeof (e as { type?: unknown }).type === 'string';
const isQueued = (q: unknown): q is QueuedEvent =>
  !!q && isEvent((q as { event?: unknown }).event)
  && ((q as { companyId?: unknown }).companyId === null || typeof (q as { companyId?: unknown }).companyId === 'string');
const isFinish = (f: unknown): f is PendingFinish =>
  !!f && typeof (f as { attemptId?: unknown }).attemptId === 'string' && Array.isArray((f as { answers?: unknown }).answers);

export class TrainingOutbox {
  private events: QueuedEvent[] = [];
  private finishes: PendingFinish[] = [];
  private companyId: string | null = null;
  private sending = false;
  private queued = false;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private retryDelay = RETRY_FIRST_MS;
  private stopped = false;

  constructor(private readonly deps: OutboxDeps) {
    this.restore();
  }

  /** Work kept from an earlier visit (or not yet sent this one). */
  hasBacklog(): boolean {
    return this.events.length > 0 || this.finishes.length > 0;
  }

  /** The company progress was read for; events added from now on are kept with it. */
  setCompany(companyId: string | null): void {
    this.companyId = companyId;
  }

  /** Events still waiting to be sent for this company, oldest first. */
  pendingEventsFor(companyId: string | null): TrainingEvent[] {
    return this.events.filter((q) => q.companyId === companyId).map((q) => q.event);
  }

  addEvents(events: TrainingEvent[]): void {
    if (!events.length) return;
    this.events.push(...events.map((event) => ({ companyId: this.companyId, event })));
    if (this.events.length > MAX_KEPT_EVENTS) this.events.splice(0, this.events.length - MAX_KEPT_EVENTS);
    this.save();
    this.schedule();
  }

  addFinish(attemptId: string, answers: FinishAnswer[]): void {
    if (this.finishes.some((f) => f.attemptId === attemptId)) return;
    this.finishes.push({ attemptId, answers });
    this.save();
    this.schedule();
  }

  /**
   * Stop sending (the page went away). Unsent work stays kept as it was: a send still in
   * flight no longer writes (a newer visit may own the kept copy now); the next visit sends
   * it again, which the server skips.
   */
  stop(): void {
    this.stopped = true;
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
  }

  /** Send everything queued, one request at a time. Never throws. */
  async flush(): Promise<void> {
    if (this.sending || this.stopped) return;
    this.sending = true;
    try {
      while (!this.stopped) {
        let failed: unknown = null;
        if (this.finishes.length) {
          const next = this.finishes[0];
          try {
            const exam = await this.deps.finishExam(next.attemptId, next.answers);
            this.deps.onFinished?.(exam);
            this.finishes.shift();
          } catch (err) {
            if (isRetryable(err)) failed = err;
            else this.finishes.shift(); // the server has it already (409) or refuses it for good
          }
        } else if (this.events.length) {
          // One company per request: the leading run of events kept for the same company.
          const company = this.events[0].companyId;
          const batch: QueuedEvent[] = [];
          for (const q of this.events) {
            if (q.companyId !== company || batch.length >= MAX_BATCH) break;
            batch.push(q);
          }
          try {
            await this.deps.postEvents(batch.map((q) => q.event), company);
            this.dropSent(batch);
          } catch (err) {
            if (isRetryable(err)) failed = err;
            else this.dropSent(batch); // refused for good, or kept for a company that is no longer theirs
          }
        } else {
          break;
        }
        this.save();
        if (failed) {
          this.retryLater();
          return;
        }
        this.retryDelay = RETRY_FIRST_MS;
      }
    } finally {
      this.sending = false;
    }
  }

  /** Send soon: everything added in the same tick goes in one request. */
  private schedule(): void {
    if (this.queued || this.sending || this.retryTimer || this.stopped) return;
    this.queued = true;
    queueMicrotask(() => {
      this.queued = false;
      void this.flush();
    });
  }

  private retryLater(): void {
    if (this.retryTimer || this.stopped) return;
    const delay = this.retryDelay;
    this.retryDelay = Math.min(this.retryDelay * 2, RETRY_MAX_MS);
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      void this.flush();
    }, delay);
  }

  /** Remove a sent batch from the front (more may have been added while it was out). */
  private dropSent(batch: QueuedEvent[]): void {
    const sent = new Set(batch.map((q) => q.event.event_id));
    let n = 0;
    while (n < this.events.length && sent.has(this.events[n].event.event_id)) n++;
    this.events.splice(0, n);
  }

  /** Read back what an earlier visit kept; an entry that isn't well formed is skipped. */
  private restore(): void {
    const { storage, storageKey } = this.deps;
    if (!storage || !storageKey) return;
    try {
      const raw = storage.getItem(storageKey);
      if (!raw) return;
      const saved = JSON.parse(raw) as Partial<Saved>;
      if (saved?.v !== 2) return;
      this.events = Array.isArray(saved.events) ? saved.events.filter(isQueued) : [];
      this.finishes = Array.isArray(saved.finishes) ? saved.finishes.filter(isFinish) : [];
    } catch {
      /* unreadable: start empty */
    }
  }

  private save(): void {
    const { storage, storageKey } = this.deps;
    if (!storage || !storageKey || this.stopped) return;
    try {
      if (!this.hasBacklog()) storage.removeItem(storageKey);
      else storage.setItem(storageKey, JSON.stringify({ v: 2, events: this.events, finishes: this.finishes } satisfies Saved));
    } catch {
      /* storage full or blocked: this visit still sends it */
    }
  }
}
